import { parseSeriesTimestamp } from './episodeDetection.js';
import {
  DURATION_FIELD_BASES,
  formatDurationMinutes,
  formatTableDurationValue,
} from './durationFormat.js';

function pad2(n) {
  return String(n).padStart(2, '0');
}

export function findRowKey(row, col) {
  if (!row || !col) return null;
  if (Object.prototype.hasOwnProperty.call(row, col)) return col;
  const lower = String(col).toLowerCase();
  return Object.keys(row).find((k) => k.toLowerCase() === lower) || null;
}

/** Claves presentes en la fila para campos temporales de episodio. */
export function resolveEpisodeTimeKeys(row) {
  const keys = {};
  for (const name of ['inicio', 'fin', 'dia', 'hora_inicio', 'duracion_min']) {
    const k = findRowKey(row, name);
    if (k) keys[name] = k;
  }
  return keys;
}

function resolveDurationColumnKey(keys) {
  for (const base of DURATION_FIELD_BASES) {
    if (keys[base]) return keys[base];
  }
  return 'duracion_min';
}

/** Formatea minutos de un tramo del timeline con el mismo estilo que la tabla de episodios. */
export function formatTimelineSegmentDuration(item, columnDurationFormats) {
  let minutes = item?.durationMin;
  if ((minutes == null || !Number.isFinite(minutes)) && Number.isFinite(item?.startHour) && Number.isFinite(item?.endHour)) {
    minutes = Math.round((item.endHour - item.startHour) * 60);
  }
  if (minutes == null || !Number.isFinite(minutes)) return '—';

  const col = item?.durationColumn || 'duracion_min';
  const formatted = formatTableDurationValue(minutes, col, columnDurationFormats, null);
  if (formatted != null) return formatted;

  if (columnDurationFormats) {
    for (const c of Object.keys(columnDurationFormats)) {
      const alt = formatTableDurationValue(minutes, c, columnDurationFormats, null);
      if (alt != null) return alt;
    }
  }

  return formatDurationMinutes(minutes, 'auto');
}

function startOfDayMs(ms) {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function formatDayFromMs(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** Etiqueta corta de día para el eje Y del mini-Gantt. */
export function formatDayLabel(ms, diaStr) {
  const raw = diaStr != null && diaStr !== '' ? String(diaStr).trim() : null;
  if (raw) {
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}`;
    if (raw.length <= 12) return raw;
    return raw.slice(0, 10);
  }
  if (ms == null) return '—';
  const d = new Date(ms);
  return `${pad2(d.getUTCDate())}/${pad2(d.getUTCMonth() + 1)}`;
}

function parseHourValue(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  const hm = s.match(/^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?/);
  if (hm) {
    return Number(hm[1]) + Number(hm[2] || 0) / 60 + Number(hm[3] || 0) / 3600;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Hora legible HH:mm desde fracción de hora del día (0–24). */
export function formatTimelineClockTimeFromHour(hour) {
  if (!Number.isFinite(Number(hour))) return '—';
  const totalMin = Math.round(Number(hour) * 60);
  const h = Math.floor(totalMin / 60) % 24;
  const m = ((totalMin % 60) + 60) % 60;
  return `${pad2(h)}:${pad2(m)}`;
}

/** Hora legible HH:mm desde timestamp UTC (ms). */
export function formatTimelineClockTimeFromMs(ms) {
  if (ms == null || !Number.isFinite(Number(ms))) return '—';
  const d = new Date(Number(ms));
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

/** Hora legible HH:mm desde ms UTC, fracción de hora o texto de timestamp. */
export function formatTimelineClockTime(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Epoch ms vs hora del día (0–24)
    if (value > 48) return formatTimelineClockTimeFromMs(value);
    return formatTimelineClockTimeFromHour(value);
  }
  const ms = parseSeriesTimestamp(value);
  if (ms != null) return formatTimelineClockTimeFromMs(ms);
  const s = String(value).trim();
  const hm = s.match(/^(\d{1,2})(?::(\d{2}))?/);
  if (hm) return `${pad2(Number(hm[1]))}:${pad2(Number(hm[2] || 0))}`;
  return s;
}

/** Parte un rango inicio–fin en segmentos por día calendario (horas 0–24). */
export function splitEpisodeHourSegments(startMs, endMs, durationColumn = 'duracion_min') {
  if (startMs == null || endMs == null || endMs <= startMs) return [];
  const segments = [];
  let cursor = startMs;
  let part = 0;
  while (cursor < endMs) {
    const dayStart = startOfDayMs(cursor);
    const nextDay = dayStart + 86400000;
    const chunkEnd = Math.min(endMs, nextDay);
    segments.push({
      id: `seg-${part}`,
      dayLabel: formatDayLabel(dayStart, formatDayFromMs(dayStart)),
      startHour: (cursor - dayStart) / 3600000,
      endHour: (chunkEnd - dayStart) / 3600000,
      startLabel: formatTimelineClockTimeFromMs(cursor),
      endLabel: formatTimelineClockTimeFromMs(chunkEnd),
      durationMin: Math.max(0, Math.round((chunkEnd - cursor) / 60000)),
      durationColumn,
    });
    cursor = chunkEnd;
    part += 1;
  }
  return segments;
}

/** Etiqueta del eje X (HH:mm o Nh). */
export function formatTimelineAxisTick(hour) {
  if (!Number.isFinite(Number(hour))) return '—';
  const totalMin = Math.round(Number(hour) * 60);
  const h = Math.floor(totalMin / 60) % 24;
  const m = totalMin % 60;
  if (m === 0) return `${pad2(h)}h`;
  return `${pad2(h)}:${pad2(m)}`;
}

function buildTimelineAxisTicks(min, max) {
  const span = max - min;
  if (span <= 0) return [min, max];

  const candidates = [0.25, 0.5, 1, 2, 3, 4, 6];
  let step = 6;
  for (const c of candidates) {
    if (span / c <= 6) {
      step = c;
      break;
    }
  }

  const ticks = [];
  let h = Math.ceil(min / step) * step;
  while (h <= max + step * 0.001) {
    ticks.push(Math.round(h * 1000) / 1000);
    h += step;
  }
  if (!ticks.length || ticks[0] > min + step * 0.01) {
    ticks.unshift(Math.round(min * 1000) / 1000);
  }
  if (ticks[ticks.length - 1] < max - step * 0.01) {
    ticks.push(Math.round(max * 1000) / 1000);
  }
  return ticks;
}

/**
 * Dominio horizontal autoajustado para el mini-Gantt (con margen alrededor de los datos).
 */
export function computeTimelineXDomain(items, options = {}) {
  const { padRatio = 0.1, minPadHours = 0.25, minSpanHours = 1 } = options;
  const fullDay = { min: 0, max: 24, ticks: [0, 6, 12, 18, 24], spanLabel: '00:00 – 24:00' };

  if (!items?.length) return fullDay;

  let dataMin = Infinity;
  let dataMax = -Infinity;
  for (const item of items) {
    if (!Number.isFinite(item.startHour) || !Number.isFinite(item.endHour)) continue;
    dataMin = Math.min(dataMin, item.startHour);
    dataMax = Math.max(dataMax, item.endHour);
  }
  if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) return fullDay;

  const dataSpan = Math.max(dataMax - dataMin, 0.01);
  const pad = Math.max(dataSpan * padRatio, minPadHours);
  let min = Math.max(0, dataMin - pad);
  let max = Math.min(24, dataMax + pad);

  if (max - min < minSpanHours) {
    const mid = (dataMin + dataMax) / 2;
    min = Math.max(0, mid - minSpanHours / 2);
    max = Math.min(24, mid + minSpanHours / 2);
    if (max - min < minSpanHours) {
      if (min <= 0.001) max = Math.min(24, minSpanHours);
      else if (max >= 24 - 0.001) min = Math.max(0, 24 - minSpanHours);
    }
  }

  if (min <= 0.01 && max >= 23.99) return fullDay;

  const ticks = buildTimelineAxisTicks(min, max);
  return {
    min,
    max,
    ticks,
    spanLabel: `${formatTimelineClockTimeFromHour(min)} – ${formatTimelineClockTimeFromHour(max)}`,
  };
}

/** Posición normalizada 0–1 de una hora dentro del dominio visible. */
export function timelineHourToRatio(hour, domain) {
  const span = domain.max - domain.min;
  if (!Number.isFinite(span) || span <= 0) return 0;
  const t = (hour - domain.min) / span;
  return Math.max(0, Math.min(1, t));
}

export function rowHasTimelineData(row) {
  if (!row || typeof row !== 'object') return false;
  const keys = resolveEpisodeTimeKeys(row);
  if (keys.inicio && keys.fin) {
    const startMs = parseSeriesTimestamp(row[keys.inicio]);
    const endMs = parseSeriesTimestamp(row[keys.fin]);
    if (startMs != null && endMs != null && endMs > startMs) return true;
  }
  if (keys.dia && keys.hora_inicio && keys.duracion_min) {
    const startHour = parseHourValue(row[keys.hora_inicio]);
    const durMin = Number(row[keys.duracion_min]);
    if (startHour != null && Number.isFinite(durMin) && durMin > 0) return true;
  }
  return false;
}

/** Segmentos horarios de una fila de episodio (0–24 h en el día indicado). */
export function parseRowTimelineSegments(row, memberIndex = 0) {
  if (!row || typeof row !== 'object') return [];
  const keys = resolveEpisodeTimeKeys(row);

  if (keys.inicio && keys.fin) {
    const startMs = parseSeriesTimestamp(row[keys.inicio]);
    const endMs = parseSeriesTimestamp(row[keys.fin]);
    if (startMs != null && endMs != null && endMs > startMs) {
      const durationColumn = resolveDurationColumnKey(keys);
      return splitEpisodeHourSegments(startMs, endMs, durationColumn).map((seg, i) => ({
        ...seg,
        id: `m${memberIndex}-${i}`,
      }));
    }
  }

  if (keys.dia && keys.hora_inicio && keys.duracion_min) {
    const startHour = parseHourValue(row[keys.hora_inicio]);
    const durMin = Number(row[keys.duracion_min]);
    if (startHour != null && Number.isFinite(durMin) && durMin > 0) {
      const endHour = Math.min(24, startHour + durMin / 60);
      const durationColumn = keys.duracion_min;
      return [
        {
          id: `m${memberIndex}-0`,
          dayLabel: formatDayLabel(null, row[keys.dia]),
          startHour,
          endHour,
          startLabel: formatTimelineClockTimeFromHour(startHour),
          endLabel: formatTimelineClockTimeFromHour(endHour),
          durationMin: Math.round(durMin),
          durationColumn,
        },
      ];
    }
  }

  return [];
}

const MAX_TIMELINE_ITEMS = 20;

/** Construye ítems del mini-Gantt a partir de los miembros del cluster/punto. */
export function buildTimelineFromMembers(members, maxItems = MAX_TIMELINE_ITEMS) {
  const items = [];
  let truncatedCount = 0;
  for (let i = 0; i < (members || []).length; i += 1) {
    const row = members[i]?.row;
    if (!row) continue;
    const segs = parseRowTimelineSegments(row, i);
    for (const seg of segs) {
      if (items.length >= maxItems) {
        truncatedCount += 1;
      } else {
        items.push(seg);
      }
    }
  }
  return {
    available: items.length > 0,
    items,
    truncatedCount,
  };
}
